import { type FunctionComponent, useCallback } from "react";

import { paywall } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import type { RefineSearchState } from "scmp-app/components/search/contexts";
import { EmptyRefineSearchState, useSearchContext } from "scmp-app/components/search/contexts";
import { PlusCheckboxField } from "scmp-app/components/search/search-result-panel/refine-search/plus-checkbox-field";
import { SectionField } from "scmp-app/components/search/search-result-panel/refine-search/section-field";
import { SortByToggle } from "scmp-app/components/search/search-result-panel/refine-search/sort-by-toggle";
import { TimeRangeField } from "scmp-app/components/search/search-result-panel/refine-search/time-range-field";
import { TypeField } from "scmp-app/components/search/search-result-panel/refine-search/type-field";

import { Container, Header, ResetButton, StyledTuneIcon, Title } from "./styles";

type Props = {
  className?: string;
  onChange?: (state: Partial<RefineSearchState>) => void;
  value?: RefineSearchState;
};

export const RefineSearchPanel: FunctionComponent<Props> = ({ className, onChange, value }) => {
  const { refineSearch, setRefineSearch } = useSearchContext();

  const state = value ?? refineSearch;
  const isPlusChecked = state.paywallTypeIds.includes(paywall.scmpPlus.entityUuid);

  const handleOnChange = useCallback(
    (state: Partial<RefineSearchState>) => {
      if (notEmpty(onChange)) {
        onChange(state);
        return;
      }
      setRefineSearch(current => ({ ...current, ...state }));
    },
    [onChange, setRefineSearch],
  );

  return (
    <Container className={className}>
      <Header>
        <StyledTuneIcon />
        <Title>Refine Search</Title>
        <ResetButton
          onClick={() => {
            setRefineSearch(EmptyRefineSearchState);
          }}
          type="button"
        >
          Reset
        </ResetButton>
      </Header>

      <SortByToggle onChange={orderBy => handleOnChange({ orderBy })} value={state.orderBy} />

      <TimeRangeField
        dateRange={state.dateRange}
        dateRangeText={state.dateRangeText}
        onChange={(dateRange, dateRangeText) => handleOnChange({ dateRange, dateRangeText })}
      />

      <SectionField
        onChange={sectionIds => handleOnChange({ sectionIds })}
        sectionIds={state.sectionIds}
      />

      <TypeField
        articleTypeIds={state.articleTypeIds}
        contentTypes={state.contentTypes}
        onChange={change => handleOnChange(change)}
      />

      <PlusCheckboxField checked={isPlusChecked} onChange={change => handleOnChange(change)} />
    </Container>
  );
};

RefineSearchPanel.displayName = "RefineSearchPanel";
